<template>
  <div class="translate-wrapper">
    <ValidationObserver
      ref="validationObserver"
      v-slot="{ handleSubmit }"
    >
      <form
        class="contact-info"
        novalidate="novalidate"
        @submit.prevent="handleSubmit(submit)"
        @input="handleFormChange"
      >
        <MaterialInput
          v-model="formData.title"
          v-bind="formFields.title"
          :disabled="isDisabled"
        />
        <MaterialTextArea
          v-model="formData.description"
          v-bind="formFields.description"
          :disabled="isDisabled"
        />
        <div class="poster-wrapper">
          <div class="poster-title">
            Poster Image (2:3)
          </div>
          <PosterImage
            v-if="mediaLanguage"
            :key="formData.image23"
            ref="posterImage"
            :poster="formData.image23"
            :onPosterChange="handlePoster23Change"
            :isNeedRemove="true"
            :isPoster2x3="true"
            :disabled="isDisabled"
            class="poster-2x3-image"
          />
        </div>
        <div class="poster-wrapper">
          <div class="poster-title">
            Poster Image (16:9)
          </div>
          <PosterImage
            v-if="mediaLanguage"
            :key="formData.image"
            ref="posterImage"
            :poster="formData.image"
            :onPosterChange="handlePosterChange"
            :isNeedRemove="true"
            :isMediaVertical="isMediaVertical"
            :disabled="isDisabled"
          />
        </div>
        <template v-if="isMediaVertical">
          <div class="poster-title">
            Poster Image (9:16)
          </div>
          <PosterImage
            v-if="mediaLanguage"
            :key="formData.image916"
            ref="posterImage916"
            :poster="formData.image916"
            :onPosterChange="handlePoster916Change"
            :isNeedRemove="true"
            :isPoster9x16="true"
            :disabled="isDisabled"
            class="poster-9x16-image"
          />
        </template>
        <div class="btn-wrapper">
          <Button
            type="submit"
            :title="'Apply Changes'"
            :classType="'primary'"
            :disabled="isDisabled"
          />
          <Button
            :title="'Revert'"
            :classType="'warning'"
            :eventFunction="revertFolderLanguage"
            :disabled="isDisabled"
          />
          <div
            v-if="isEditedForm.length
              && isEditedForm.find(item => item.id === lang.id && !!item.value)"
            class="changes-wrapper"
          >
            <inline-svg
              :src="iconChanges"
              class="svg-icon"
            >
            </inline-svg>
            <span class="changes-text">
              Unsaved changes
            </span>
          </div>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import BaseForm from '../BaseForm.vue';
import MaterialInput from '../../inputs/MaterialInput.vue';
import MaterialTextArea from '../../inputs/MaterialTextArea.vue';
import PosterImage from '../../common/PosterImage/DragAndDropFileUploader.vue';
import Button from '../../common/Button.vue';
import iconChanges from '../../../assets/icons/icon-localization-changes.svg';

import {
  ADD_LANGUAGE_MEDIA_REQUEST,
  GET_LANGUAGE_MEDIA_REQUEST,
  INIT_FORM_CHANGES,
  SET_FORM_CHANGES,
} from '../../../store/actions/localization/localizationActions';

export default {
  name: 'TranslateMediaForm',
  components: {
    MaterialInput,
    MaterialTextArea,
    PosterImage,
    Button,
  },
  extends: BaseForm,
  props: {
    formPrefix: {
      type: String,
      default: 'mediaLanguage',
    },
    media: {
      type: Object,
      default: () => {},
    },
    lang: {
      type: Object,
      default: () => {},
    },
    selected: {
      type: Boolean,
      default: false,
    },
    enabledLanguages: {
      type: Array,
      default: () => [],
    },
    changedLanguage: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      iconChanges,
      submitEvent: ADD_LANGUAGE_MEDIA_REQUEST,
      successMessages: `The ${this.lang.name} Localization has been successfully updated`,
      isPosterSmallChanged: false,
      isPoster23Changed: false,
      formData: {
        title: '',
        description: '',
        image: '',
        image23: '',
        image916: '',
      },
      formFields: {
        title: {
          type: 'text',
          name: 'title',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 255,
          },
        },
        description: {
          type: 'text',
          name: 'description',
          formPrefix: this.formPrefix,
          rules: {
            required: false,
            max: 1800,
          },
        },
      },
      errorMapping: {
        title: [
          `${this.formPrefix}.title`,
        ],
        description: [
          `${this.formPrefix}.description`,
        ],
      },
    };
  },
  computed: {
    ...mapState({
      mediaLanguage: (state) => state.localization.mediaLanguage,
      isEditedForm: (state) => state.localization.isEditedForm,
    }),
    ...mapGetters([
      'getCurrentAccountId',
      'isContributor',
    ]),
    isMediaVertical() {
      return this.media.orientation === 'vertical';
    },
    isDisabled() {
      return this.isContributor && (this.media.approved || this.media.locked);
    },
  },
  created() {
    const value = this.enabledLanguages.map(((item) => ({
      id: item.id,
      value: false,
    })));
    this.$store.dispatch(INIT_FORM_CHANGES, value);

    const params = {
      accountId: this.getCurrentAccountId,
      mediaId: this.media.id,
      langId: this.lang.id,
    };
    this.$store.dispatch(GET_LANGUAGE_MEDIA_REQUEST, params).then(() => {
      this.initFormData();
    });
  },
  beforeDestroy() {
    this.$store.dispatch(SET_FORM_CHANGES, []);
  },
  methods: {
    handleFormChange() {
      this.$emit('changedLanguage', { id: this.lang.id, value: true });
    },
    initFormData() {
      this.formData.title = this.mediaLanguage.name;
      this.formData.description = this.mediaLanguage.description;
      this.formData.image = this.mediaLanguage.posterImage
        ? `${this.mediaLanguage.posterImage}?height=360`
        : '';
      this.formData.image23 = this.mediaLanguage.posterImage2x3
        ? `${this.mediaLanguage.posterImage2x3}?height=360`
        : '';
      this.formData.image916 = this.mediaLanguage.posterImage9x16
        ? `${this.mediaLanguage.posterImage9x16}?height=360`
        : '';
    },
    handlePosterChange(img) {
      this.isPosterSmallChanged = true;
      this.handleFormChange();
      this.formData.image = img;
    },
    handlePoster23Change(img) {
      this.isPoster23Changed = true;
      this.handleFormChange();
      this.formData.image23 = img;
    },
    handlePoster916Change(img) {
      this.isPoster916Changed = true;
      this.handleFormChange();
      this.formData.image916 = img;
    },
    revertFolderLanguage() {
      const params = {
        accountId: this.getCurrentAccountId,
        mediaId: this.media.id,
        langId: this.lang.id,
      };
      this.$store.dispatch(GET_LANGUAGE_MEDIA_REQUEST, params).then(() => {
        this.$emit('changedLanguage', { id: this.lang.id, value: false });
        this.$store.dispatch(SET_FORM_CHANGES, { id: this.lang.id, value: false });
        this.isPosterSmallChanged = false;
        this.isPoster23Changed = false;
        this.initFormData();
      });
    },
    getRequestData() {
      const data = {
        name: this.formData.title,
        description: this.formData.description,
        posterImage: this.formData.image,
        posterImage2x3: this.formData.image23,
        posterImage9x16: this.formData.image916,
      };

      if (!this.isPosterSmallChanged) {
        delete data.posterImage;
      }

      if (!this.isPoster23Changed) {
        delete data.posterImage2x3;
      }
      if (!this.isPoster916Changed) {
        delete data.posterImage9x16;
      }

      return {
        accountId: this.getCurrentAccountId,
        mediaId: this.media.id,
        langId: this.lang.id,
        data,
      };
    },
    onSubmitSuccess() {
      this.isPosterSmallChanged = false;
      this.isPoster23Changed = false;
      this.$emit('changedLanguage', { id: this.lang.id, value: false });
      this.$store.dispatch(SET_FORM_CHANGES, { id: this.lang.id, value: false });
      this.$toasted.global.success({
        message: this.successMessages,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.translate-wrapper {
  padding-left: 20px;
  padding-right: 428px;
}

.poster-wrapper {
  margin-bottom: 30px;
}

.poster-title {
  color: #fff;
  font-weight: normal;
  margin-bottom: 10px;
}

.btn-wrapper {
  display: flex;
  padding-top: 35px;
}

.changes-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #FB3951;
  border-radius: 2px;
  padding: 8px;
  color: #FB3951;
  margin-left: 20px;
}

.changes-text {
  padding-left: 5px;
}

@media all and (max-width: 1670px) {
  .translate-wrapper {
    padding-right: 300px;
  }
}

@media all and (max-width: 1460px) {
  .translate-wrapper {
    padding-right: 200px;
  }
}

@media all and (max-width: 1300px) {
  .translate-wrapper {
    padding-right: 100px;
  }
}
.poster-2x3-image {
  height: 310px;
}

.poster-9x16-image {
  height: 310px;
}
</style>
